import { useCallback, useEffect, useState } from "react";
import { Building2, Users, Boxes, Search, CheckCircle2, RefreshCw } from "lucide-react";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { StatCard } from "../components/ui/StatCard";
import { PageHeader } from "../components/ui/PageHeader";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CompaniesTab, type Company } from "../components/superadmin/CompaniesTab";
import { UsersTab, type UserRow } from "../components/superadmin/UsersTab";
import { ModulesTab, type PackageRow } from "../components/superadmin/ModulesTab";

type Tab = "companies" | "users" | "modules";

export default function SuperAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("companies");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const reload = useCallback(async () => {
    const [compRes, userRes, pkgRes] = await Promise.all([
      api.get("/api/superadmin/companies"),
      api.get("/api/superadmin/users"),
      api.get("/api/superadmin/packages"),
    ]);
    setCompanies(compRes.data);
    setUsers(userRes.data);
    setPackages(pkgRes.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, [reload]);

  const tabs: { id: Tab; label: string; icon: typeof Building2 }[] = [
    { id: "companies", label: "Bedrijven", icon: Building2 },
    { id: "users", label: "Gebruikers", icon: Users },
    { id: "modules", label: "Modules & Kortingen", icon: Boxes },
  ];

  const activePackages = packages.filter((p) => p.isActive);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Superadmin"
        subtitle="Beheer bedrijven, gebruikers, pakketten en kortingen"
        action={
          <Button variant="secondary" onClick={reload} loading={loading}>
            <RefreshCw size={27} /> Verversen
          </Button>
        }
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label="Bedrijven" value={companies.length} icon={Building2} color="brand" delay={0} />
        <StatCard label="Gebruikers" value={users.length} icon={Users} color="indigo" delay={0.08} />
        <StatCard
          label="Actieve bedrijven"
          value={companies.filter((c) => c.isActive).length}
          icon={CheckCircle2}
          color="emerald"
          delay={0.16}
        />
        <StatCard
          label="Pakketten"
          value={packages.length}
          icon={Boxes}
          color="purple"
          delay={0.24}
        />
      </div>

      <Card className="p-3">
        <div className="flex justify-center gap-2 sm:gap-3 overflow-x-auto pb-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                setSearch("");
              }}
              className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap min-w-[150px] transition-all ${
                tab === t.id
                  ? "bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-lg shadow-brand-500/20"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <t.icon size={24} />
              {t.label}
            </button>
          ))}
        </div>
        {tab !== "modules" && (
          <div className="mt-3 flex justify-center">
            <div className="search-chip w-full max-w-xs">
              <Search size={27} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoeken..."
                className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
              />
            </div>
          </div>
        )}
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
        </div>
      ) : tab === "companies" ? (
        <CompaniesTab
          companies={companies}
          packages={activePackages}
          users={users}
          search={search}
          reload={reload}
        />
      ) : tab === "users" ? (
        <UsersTab
          users={users}
          companies={companies}
          search={search}
          currentUserId={user?.id || ""}
          reload={reload}
        />
      ) : (
        <ModulesTab packages={packages} reload={reload} />
      )}
    </div>
  );
}